import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { CircularProgress } from '../components/CircularProgress';

import { usePostLoginRedirect } from './usePostLoginRedirect';
import { POST_LOGIN_ROUTES } from './session/post-login-redirect';
import { savePrimeiroAcesso } from './session/primeiro-acesso-storage';

// Mesmo destino do RequireAuth/AuthInterceptor quando não há sessão utilizável.
const LOGIN_ROUTE = '/pages/login';

const PATH_INTEGRADOR_KEY = 'pathIntegrador';

function readPathIntegrador(): string | null {
  try {
    return localStorage.getItem(PATH_INTEGRADOR_KEY);
  } catch {
    return null;
  }
}

function consumePathIntegrador(): void {
  try {
    localStorage.removeItem(PATH_INTEGRADOR_KEY);
  } catch {
    // Storage indisponível — best-effort.
  }
}

/**
 * Landing pós-login: resolve a home do usuário (mesma árvore do `goHome` legado)
 * e redireciona para lá. Exibe um spinner enquanto os dados do morador carregam.
 * Sem destino (sessão inutilizável), cai no login como o RequireAuth.
 *
 * A navegação é uma escrita direta de `window.location.hash` (a convenção de
 * travessia da fronteira React↔Angular), não `navigate()`: '/' é uma rota
 * transitória e o AngularHost do login está sendo desmontado neste mesmo tick.
 * O `navigate` (history.replace) disputaria o hash com a `HashLocationStrategy`
 * do Angular sendo destruída e a URL voltaria para '/'. Adiar a escrita para o
 * próximo tick garante que ela assente após o teardown síncrono, e o ref
 * garante disparo único (inclusive sob StrictMode).
 */
export function PostLoginRedirect(): JSX.Element {
  const { ready, destination } = usePostLoginRedirect();
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!ready || redirectedRef.current) {
      return;
    }
    redirectedRef.current = true;

    if (destination) {
      // Deep link de integrador é de uso único, como no goHome legado: só
      // consome quando o destino resolvido é de fato o próprio deep link.
      if (destination === readPathIntegrador()) {
        consumePathIntegrador();
      }
      // O goHome legado gravava esta flag antes de ir ao wizard; sem ela o
      // FullLayout renderiza sidebar/navbar por cima do wizard de primeiro acesso.
      savePrimeiroAcesso(destination === POST_LOGIN_ROUTES.wizard);
    }

    const target = destination ?? LOGIN_ROUTE;
    const timeoutId = setTimeout(() => {
      window.location.hash = `#${target}`;
    }, 0);
    return () => {
      clearTimeout(timeoutId);
      redirectedRef.current = false;
    };
  }, [ready, destination]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
}
